import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, XCircle, AlertTriangle } from "lucide-react";
import { ROUTES_CONFIG } from "src/constants/routesConfig";
import { TransitProductAmendmentInputField } from "src/types";

interface ModalProps {
  purchaseOrderId: string;
  onClose?: () => void;
  productAmendments?: TransitProductAmendmentInputField[];
}

interface SuccessProps extends ModalProps {}

interface WarningProps extends ModalProps {
  onCancel: () => void;
  onConfirm: () => void;
}

interface ErrorProps extends ModalProps {
  onRetry: () => void;
}

interface ProductDifference {
  id: string;
  name?: string;
  skuName?: string;
  warehouseLogQuantity: number;
  receivedQuantity: number;
  difference: number;
}

const Success: React.FC<SuccessProps> = ({ purchaseOrderId, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <CheckCircle2 className="w-6 h-6 text-success-600" />
            <h2 className="text-lg font-semibold text-gray-900">Transit Purchase Order Amended</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-success-50 border border-success-200 rounded-lg p-4">
            <p className="text-sm text-gray-700">
              Transit purchase order has been successfully amended{" "}
              <span className="font-semibold text-success-700">(ID: {purchaseOrderId})</span>
            </p>
          </div>

          {/* Home Button */}
          <div className="flex justify-center pt-2">
            <Link to={ROUTES_CONFIG.transitPurchaseOrdersDashboard.path}>
              <button className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium">
                Home
              </button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const Confirmation: React.FC<WarningProps> = ({ purchaseOrderId, onConfirm, onCancel, productAmendments = [] }) => {
  // Calculate product differences - show all products with differences from warehouse log
  const productDifferences: ProductDifference[] = productAmendments
    .map((product) => {
      const amendedReceivedQty = parseInt(product.amendedReceivedQty, 10) || 0;
      const warehouseLogQty = product.warehouseLogQuantity ?? 0;

      const difference = amendedReceivedQty - warehouseLogQty;

      // Only include products that have a difference from warehouse log
      if (difference === 0) {
        return null;
      }

      return {
        id: product.id,
        name: product.name,
        skuName: product.skuName,
        warehouseLogQuantity: warehouseLogQty,
        receivedQuantity: amendedReceivedQty,
        difference
      };
    })
    .filter((product) => product !== null) as ProductDifference[];

  const hasDifferences = productDifferences.length > 0;
  const allQuantitiesMatch = !hasDifferences;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-warning-600" />
            <h2 className="text-lg font-semibold text-gray-900">Confirm Amendment</h2>
          </div>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
            &times;
          </button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto flex-1">
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
            <p className="text-sm font-semibold text-warning-800 mb-1">
              Are you sure you want to amend this transit purchase order?
            </p>
            <p className="text-sm text-warning-700">ID: {purchaseOrderId}</p>
          </div>

          {allQuantitiesMatch ? (
            <div className="bg-success-50 border border-success-200 rounded-lg p-4">
              <div className="flex items-start">
                <CheckCircle2 className="w-5 h-5 text-success-600 mt-0.5 mr-2 flex-shrink-0" />
                <div>
                  <p className="text-sm font-semibold text-success-800 mb-1">
                    All quantities match warehouse log quantities
                  </p>
                  <p className="text-sm text-success-700">
                    This transit purchase order will be marked as <span className="font-semibold">Closed</span> after
                    the amendment.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
                <p className="text-sm font-semibold text-warning-800 mb-2">
                  The following products have quantity differences:
                </p>
              </div>

              {/* Product Differences Table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Product ID</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Product Name</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-700">Warehouse Log Qty</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-700">Received Qty</th>
                        <th className="px-4 py-3 text-center font-semibold text-gray-700">Difference</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {productDifferences.map((product) => (
                        <tr key={product.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 font-medium text-gray-900">{product.id}</td>
                          <td className="px-4 py-3 text-gray-700">{product.name || product.skuName || "-"}</td>
                          <td className="px-4 py-3 text-center text-gray-700">{product.warehouseLogQuantity}</td>
                          <td className="px-4 py-3 text-center text-gray-700">{product.receivedQuantity}</td>
                          <td
                            className={`px-4 py-3 text-center font-semibold ${
                              product.difference > 0 ? "text-success-600" : "text-error-600"
                            }`}
                          >
                            {product.difference > 0 ? "+" : ""}
                            {product.difference}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          <p className="text-sm text-gray-600 text-center font-medium">This action will update the transit status</p>
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end space-x-3 p-5 border-t border-gray-200 bg-gray-50">
          <button
            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors text-sm font-medium"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
            onClick={onConfirm}
          >
            Confirm Amendment
          </button>
        </div>
      </div>
    </div>
  );
};

const Error: React.FC<ErrorProps> = ({ onRetry, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-3">
            <XCircle className="w-6 h-6 text-error-600" />
            <h2 className="text-lg font-semibold text-gray-900">Amendment Failed</h2>
          </div>
          {onClose && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
              &times;
            </button>
          )}
        </div>

        <div className="p-5">
          <p className="text-sm text-gray-700 text-center mb-6">
            An error occurred while amending the transit purchase order. Please try again.
          </p>

          {/* Footer Button */}
          <div className="flex justify-center">
            <button
              className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors text-sm font-medium"
              onClick={onRetry}
            >
              Retry Amendment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AmendTransitPurchaseOrderModals = {
  Success,
  Confirmation,
  Error
};

export { AmendTransitPurchaseOrderModals };
